import { useCallback, useEffect, useState } from "react";
import { Clock, Mail, Plus, ShieldCheck, Trash2 } from "lucide-react";
import { api } from "@/api/client";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { CopyButton } from "@/components/ui/copy-button";
import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/ui/spinner";
import { toastApiError, toastApiSuccess } from "@/lib/api-toast";
import type { components } from "@dolfin/api-admin";

type BillAllowedSenderResponse = components["schemas"]["BillAllowedSenderResponse"];
type BillForwardingRejectionResponse = components["schemas"]["BillForwardingRejectionResponse"];

const dateTimeFmt = new Intl.DateTimeFormat("en-GB", { dateStyle: "medium", timeStyle: "short" });

/** Forwarding address, allowed senders and rejections on one screen: a company has no users, so every forward
 * is rejected until a sender is allowed. */
export function CompanyForwardingSection({ companyId }: { companyId: string }) {
  const [address, setAddress] = useState<string | null>(null);
  const [addressLoading, setAddressLoading] = useState(true);

  const [allowed, setAllowed] = useState<BillAllowedSenderResponse[] | null>(null);
  const [rejections, setRejections] = useState<BillForwardingRejectionResponse[] | null>(null);
  const [listError, setListError] = useState<string | null>(null);

  const [newSender, setNewSender] = useState("");
  const [adding, setAdding] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setAddressLoading(true);
    (async () => {
      // Allowed to fail quietly: companies created before bill forwarding have no address.
      const { data } = await api.GET("/v1/companies/{companyId}/inbound-email-address", {
        params: { path: { companyId } },
      });
      if (cancelled) return;
      setAddress(data?.address ?? null);
      setAddressLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [companyId]);

  const refreshLists = useCallback(async () => {
    const [sendersRes, rejectionsRes] = await Promise.all([
      api.GET("/v1/companies/{companyId}/allowed-senders", { params: { path: { companyId } } }),
      api.GET("/v1/companies/{companyId}/forwarding-rejections", {
        params: { path: { companyId }, query: { Limit: 50 } },
      }),
    ]);

    if (sendersRes.error || !sendersRes.data) {
      setListError("Couldn't load the allowed senders.");
      return;
    }
    setAllowed(sendersRes.data);
    setRejections(rejectionsRes.error || !rejectionsRes.data ? [] : rejectionsRes.data);
    setListError(null);
  }, [companyId]);

  useEffect(() => {
    void refreshLists();
  }, [refreshLists]);

  async function addSender(rawValue: string) {
    const value = rawValue.trim().toLowerCase();
    if (!value) return;
    // An "@" means one address, otherwise a whole domain (same as the sandbox page).
    const kind = value.includes("@") ? "Address" : "Domain";

    setAdding(true);
    try {
      const { error, response } = await api.POST("/v1/companies/{companyId}/allowed-senders", {
        params: { path: { companyId } },
        body: { kind, value },
      });
      if (error) {
        await toastApiError({ error, response }, "Couldn't allow that sender.");
        return;
      }
      setNewSender("");
      toastApiSuccess(`Allowed ${value}`, "Their future forwards will be imported.");
      await refreshLists();
    } finally {
      setAdding(false);
    }
  }

  async function removeSender(senderId: string, value: string) {
    setBusyId(senderId);
    try {
      const { error, response } = await api.DELETE(
        "/v1/companies/{companyId}/allowed-senders/{senderId}",
        { params: { path: { companyId, senderId } } },
      );
      if (error) {
        await toastApiError({ error, response }, "Couldn't remove that sender.");
        return;
      }
      toastApiSuccess(`Removed ${value}`);
      await refreshLists();
    } finally {
      setBusyId(null);
    }
  }

  // A rejection whose sender is now allowed is spent; the backend clears it on the next forward, this hides it now.
  const isNowAllowed = (r: BillForwardingRejectionResponse) =>
    (allowed ?? []).some(
      (s) =>
        (s.kind === "Address" && s.value === r.fromAddress) ||
        (s.kind === "Domain" && s.value === r.fromDomain),
    );
  const waiting = rejections?.filter((r) => !isNowAllowed(r)) ?? null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Mail className="size-4" /> Bill forwarding
        </CardTitle>
        <CardDescription>
          Forward this company's accounts-payable mailbox here. Each bill is read and assigned to the
          organisation its delivery address belongs to; anything that cannot be matched confidently
          waits below.
        </CardDescription>
      </CardHeader>

      <CardContent className="flex flex-col gap-6">
        <div>
          {addressLoading ? (
            <div className="flex items-center justify-center py-4">
              <Spinner className="size-4" />
            </div>
          ) : address ? (
            <div className="flex items-center gap-2 rounded-md border border-border bg-muted/30 px-3 py-2">
              <code className="flex-1 break-all text-sm">{address}</code>
              <CopyButton value={address} />
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              This company does not have a forwarding address yet.
            </p>
          )}
        </div>

        <div>
          <div className="mb-1 flex items-center gap-2 text-sm font-semibold">
            <ShieldCheck className="size-4 text-muted-foreground" /> Allowed senders
          </div>
          <p className="mb-3 text-sm text-muted-foreground">
            Only these can forward bills to the address above. A company has no users of its own, so
            unlike an organisation nobody is allowed implicitly — if this list is empty, every forward
            is turned away.
          </p>

          <form
            className="mb-3 flex items-center gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              void addSender(newSender);
            }}
          >
            <Input
              value={newSender}
              placeholder="accounts@supplier.com  or  supplier.com"
              disabled={adding}
              onChange={(e) => setNewSender(e.target.value)}
              className="max-w-sm font-mono text-sm"
            />
            <Button type="submit" disabled={adding || !newSender.trim()}>
              {adding ? <Spinner className="size-3.5" /> : <Plus className="size-3.5" />} Allow
            </Button>
          </form>

          {listError ? (
            <p className="text-sm text-destructive">{listError}</p>
          ) : allowed === null ? (
            <div className="flex items-center justify-center py-4">
              <Spinner className="size-4" />
            </div>
          ) : allowed.length === 0 ? (
            <p className="rounded-md border border-dashed px-4 py-5 text-center text-sm text-muted-foreground">
              Nobody is allowed yet, so no forwarded bill will be imported. Add the mailbox your
              suppliers send from.
            </p>
          ) : (
            <ul className="divide-y rounded-md border border-border">
              {allowed.map((s) => (
                <li key={s.id} className="flex items-center gap-3 px-3 py-2">
                  <Badge variant="secondary" className="shrink-0">
                    {s.kind === "Domain" ? "Domain" : "Email"}
                  </Badge>
                  <code className="flex-1 truncate text-sm">{s.value}</code>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    title={`Remove ${s.value}`}
                    disabled={busyId === s.id}
                    onClick={() => s.id && void removeSender(s.id, s.value)}
                  >
                    {busyId === s.id ? (
                      <Spinner className="size-3.5" />
                    ) : (
                      <Trash2 className="size-3.5" />
                    )}
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div>
          <div className="mb-1 flex items-center gap-2 text-sm font-semibold">
            <Clock className="size-4 text-muted-foreground" /> Senders waiting
          </div>
          <p className="mb-3 text-sm text-muted-foreground">
            Forwards that were turned away because the sender is not allowed yet, or the email failed
            authentication. Allowing a sender imports their{" "}
            <span className="text-foreground">future</span> forwards — the held ones are not kept, so
            ask them to send again.
          </p>

          {waiting === null ? (
            <div className="flex items-center justify-center py-4">
              <Spinner className="size-4" />
            </div>
          ) : waiting.length === 0 ? (
            <p className="rounded-md border border-dashed px-4 py-5 text-center text-sm text-muted-foreground">
              Nothing waiting — no forwards have been turned away.
            </p>
          ) : (
            <ul className="divide-y rounded-md border border-border">
              {waiting.map((r) => {
                // Typed `number | string` in the spec, so coerce.
                const attachments = Number(r.attachmentCount);
                return (
                <li key={r.id} className="flex items-center gap-3 px-3 py-2.5">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <code className="truncate text-sm">{r.fromAddress}</code>
                      {r.reason === "AuthenticationFailed" && (
                        <Badge variant="destructive" className="shrink-0">
                          {r.authResult === "Fail" ? "Failed authentication" : "Unverified"}
                        </Badge>
                      )}
                    </div>
                    <p className="truncate text-xs text-muted-foreground">
                      {r.subject || "(no subject)"}
                      {attachments > 0 &&
                        ` · ${attachments} attachment${attachments === 1 ? "" : "s"}`}
                      {r.receivedAt && ` · ${dateTimeFmt.format(new Date(r.receivedAt))}`}
                    </p>
                  </div>
                  {/* Allow the address, not the domain: the least surprising thing for one click. */}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="shrink-0"
                    disabled={adding}
                    onClick={() => void addSender(r.fromAddress)}
                  >
                    Allow sender
                  </Button>
                </li>
                );
              })}
            </ul>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
